import React, { createContext, useContext, useMemo } from 'react';
import { Alert, StatusIndicator } from '@cloudscape-design/components';
import CompanyEmailInterstitial from '@@/products/portal/pages/company-id-interstitial';
import AppProvider from '@@/contexts/AppProvider';
import { useUserPoolConfig } from '@@/hooks/use-user-pool-config';
import { ResourcesConfig } from 'aws-amplify';
import cfg from 'envConfig';
import { AppContext } from '@@/contexts/AppContext';

interface CompanyEmailContextType {
  clearCompanyEmail: () => void;
  companyEmail: string | null;
}

const CompanyEmailContext = createContext<CompanyEmailContextType | null>(null);

export const useCompanyEmail = () => {
  const context = useContext(CompanyEmailContext);
  if (!context) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};

interface CompanyEmailProviderProps {
  children: React.ReactNode;
}

// No Amplify configuration needed since outage notifications are public endpoints
function MinimalAppContextProvider({ children }: { children: React.ReactNode }) {
  const config = useMemo<ResourcesConfig>(() => {
    return {
      API: {
        REST: {
          OutageNotificationsApi: {
            endpoint: cfg.outageNotificationsApi,
            region: cfg.auth?.region
          }
        }
      }
    };
  }, []);

  return <AppContext.Provider value={config}>{children}</AppContext.Provider>;
}

export default function CompanyEmailProvider({ children }: CompanyEmailProviderProps) {
  const { userPoolConfig, isLoading, error, companyEmail, showInterstitial, handleEmailSubmit, clearCompanyEmail } =
    useUserPoolConfig();

  if (!companyEmail || showInterstitial || !userPoolConfig) {
    return (
      <MinimalAppContextProvider>
        <CompanyEmailInterstitial onSubmit={handleEmailSubmit} isLoading={isLoading} error={error} />
      </MinimalAppContextProvider>
    );
  }

  if (isLoading) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <StatusIndicator type='loading'>Loading...</StatusIndicator>
      </div>
    );
  }

  if (error && !userPoolConfig) {
    return (
      <div
        style={{
          height: '100%',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <Alert header='Configuration error' type='error'>
          Error loading application configuration. Please try again or contact support.
        </Alert>
      </div>
    );
  }

  return (
    <CompanyEmailContext.Provider value={{ clearCompanyEmail, companyEmail }}>
      <AppProvider>{children}</AppProvider>
    </CompanyEmailContext.Provider>
  );
}
